import { InjectionToken } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Prefijo único bajo el que vive toda la API del motor en apps/api (`src/common/http/api-prefix.ts`):
 * `/agents-engine/agents`, `/agents-engine/runs`…
 */
export const API_PREFIX = '/agents-engine';

/** Base absoluta de la API del motor a partir del origen de la API de Sirius (`environment.APIEndPoint`, sin barra final). */
export function engineApiBaseUrl(apiEndPoint: string): string {
  return `${apiEndPoint.replace(/\/+$/, '')}${API_PREFIX}`;
}

/**
 * `${environment.APIEndPoint}/agents-engine`: la única URL a la que el interceptor compuesto
 * (`agents-engine.interceptor.ts`) añade `Authorization`/`x-request-id` y traduce los errores.
 */
export const ENGINE_API_BASE_URL = engineApiBaseUrl(environment.APIEndPoint);

/** Configuración estática de la feature (sin secretos). */
export interface AppConfig {
  /**
   * Base de la API, antepuesta a cada ruta (`/agents`, `/runs`, …). Absoluta: apps/web siempre llama a la API
   * cross-origin (`environment.APIEndPoint`), el motor va bajo su prefijo `/agents-engine`.
   */
  apiBaseUrl: string;
  /** Prefijo de las claves de `localStorage` / `sessionStorage`. */
  storagePrefix: string;
  /** Cadencia del pulso (`/health/ready`) con la pestaña visible. */
  pulseIntervalMs: number;
  /** Umbral a partir del cual se muestran esqueletos de carga. */
  skeletonDelayMs: number;
}

export const DEFAULT_APP_CONFIG: AppConfig = {
  apiBaseUrl: ENGINE_API_BASE_URL,
  storagePrefix: 'ae.',
  pulseIntervalMs: 30_000,
  skeletonDelayMs: 150,
};

export const APP_CONFIG = new InjectionToken<AppConfig>('AGENTS_ENGINE_APP_CONFIG', {
  providedIn: 'root',
  factory: () => DEFAULT_APP_CONFIG,
});

/**
 * Clase de los overlays de la feature (notificaciones, drawers, modales, dropdowns), que la CDK pinta fuera de
 * `.ae-root`: los estilos `.ant-*` propios de la consola se acotan a `.ae-root` y `.ae-overlay` (`styles/`).
 */
export const AE_OVERLAY_CLASS = 'ae-overlay';

/**
 * Claves persistidas en el navegador (ux-spec §8.11). Nunca datos de negocio; las claves por slug/id van con
 * `AuthService.scopedKey()` (`org:<id>:…`). El token no se guarda aquí: la sesión es la de Sirius
 * (`UserSettingsService`); tema y densidad no existen en Sirius.
 */
export const STORAGE_KEYS = {
  sider: 'ae.sider',
  onboarding: 'ae.onboarding',
} as const;
